<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, type ComponentPublicInstance, watch } from 'vue'
import { scrollTabToCenter } from './tabsScroll'

export type CommonTabItem = {
  label: string
  value: string | number
}

const props = withDefaults(
  defineProps<{
    tabs: CommonTabItem[]
    modelValue: CommonTabItem['value']
    ariaLabel?: string
    disabled?: boolean
    maskWidth?: number
  }>(),
  {
    ariaLabel: 'Tabs',
    disabled: false,
    maskWidth: 32,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: CommonTabItem['value']]
  change: [tab: CommonTabItem, index: number]
}>()

const tabsRef = ref<HTMLElement | null>(null)
const tabButtonRefs = ref<Array<HTMLElement | null>>([])
const showOverflowMask = ref(false)
let tabsResizeObserver: ResizeObserver | null = null

/** 保存每个 Tab 按钮的 DOM 引用，以支持选中项自动滚动和键盘焦点循环。 */
const setTabButtonRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (el instanceof HTMLElement) {
    tabButtonRefs.value[index] = el
    return
  }

  tabButtonRefs.value[index] = null
}

/** 根据首尾 Tab 的实际位置决定是否展示溢出渐隐遮罩。 */
const updateTabMask = () => {
  const tabsEl = tabsRef.value

  if (!tabsEl) {
    showOverflowMask.value = false
    return
  }

  const firstTab = tabButtonRefs.value[0]
  const lastTab = tabButtonRefs.value[props.tabs.length - 1]

  if (!firstTab || !lastTab) {
    showOverflowMask.value = false
    return
  }

  const tabsRect = tabsEl.getBoundingClientRect()
  const isRtl = getComputedStyle(tabsEl).direction === 'rtl'
  const edgeTolerance = 1

  showOverflowMask.value = isRtl
    ? lastTab.getBoundingClientRect().left < tabsRect.left - edgeTolerance
    : lastTab.getBoundingClientRect().right > tabsRect.right + edgeTolerance
}

/** 将选中项滚动到容器中间，方便用户继续查看选中状态及相邻 Tab。 */
const scrollTabIntoView = (index: number) => {
  const tabsEl = tabsRef.value
  const targetTab = tabButtonRefs.value[index]

  if (!tabsEl || !targetTab) {
    return
  }

  scrollTabToCenter(tabsEl, targetTab)
}

/** 选择 Tab 并向父组件同步值及完整的 Tab 数据。 */
const selectTab = async (tab: CommonTabItem, index: number) => {
  if (props.disabled) {
    return
  }

  emit('update:modelValue', tab.value)
  emit('change', tab, index)

  await nextTick()
  scrollTabIntoView(index)
  requestAnimationFrame(updateTabMask)
}

/** 键盘焦点到达最后一个分类后，按 Tab 从首个分类继续循环。 */
const onTabKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key !== 'Tab' || event.shiftKey || index !== props.tabs.length - 1 || props.tabs.length < 2) {
    return
  }

  event.preventDefault()
  tabButtonRefs.value[0]?.focus()
}

watch(
  () => props.tabs,
  () => {
    void nextTick(updateTabMask)
  },
  { deep: true },
)

onMounted(() => {
  void nextTick(updateTabMask)
  const tabsEl = tabsRef.value

  tabsEl?.addEventListener('scroll', updateTabMask, { passive: true })
  window.addEventListener('resize', updateTabMask)

  if (tabsEl && typeof ResizeObserver !== 'undefined') {
    tabsResizeObserver = new ResizeObserver(updateTabMask)
    tabsResizeObserver.observe(tabsEl)
  }
})

onBeforeUnmount(() => {
  tabsRef.value?.removeEventListener('scroll', updateTabMask)
  window.removeEventListener('resize', updateTabMask)
  tabsResizeObserver?.disconnect()
  tabsResizeObserver = null
})
</script>

<template>
  <div class="common-tabs-wrap" :style="{ '--common-tabs-mask-width': `${maskWidth}px` }">
    <div ref="tabsRef" class="common-tabs" role="tablist" :aria-label="ariaLabel">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.value"
        :ref="(el) => setTabButtonRef(el, index)"
        type="button"
        class="common-tab cursor-pointer hover:bg-[#F8F8F8]"
        :class="{ 'is-active': tab.value === modelValue }"
        role="tab"
        :aria-selected="tab.value === modelValue"
        :aria-disabled="disabled"
        :disabled="disabled"
        @click="selectTab(tab, index)"
        @keydown="onTabKeydown($event, index)"
      >
        <slot name="label" :tab="tab" :index="index">{{ tab.label }}</slot>
      </button>
    </div>
    <div v-show="showOverflowMask" class="common-tabs-mask" aria-hidden="true"></div>
  </div>
</template>

<style scoped lang="less">
.common-tabs-wrap {
  position: relative;
  width: var(--common-tabs-width, 1100px);
  max-width: var(--common-tabs-max-width, calc(100% - 100px));
  min-width: 0;
  flex: 0 1 auto;
  padding: 0 2px;
}

.common-tabs {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: var(--common-tabs-padding, 12px 0);
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.common-tab {
  flex-shrink: 0;
  box-sizing: border-box;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 2px;
  background: #fff;
  color: #292a2e;
  padding: var(--common-tab-padding, 8px 12px);
  font-size: 13px;
  font-weight: var(--common-tab-font-weight, 400);
  line-height: 20px;

  &:not(:last-child) {
    margin-inline-end: var(--common-tabs-gap, 8px);
  }

  &:hover:not([aria-disabled='true']) {
    background: #f8f8f8;
  }

  &:active:not([aria-disabled='true']) {
    background: #f0f1f2;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.3);
  }

  &.is-active {
    border-color: #112d73;
    background: #112d73;
    color: #fff;
    font-weight: 500;

    &:hover:not([aria-disabled='true']) {
      background: linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), #112d73;
    }
  }

  &[aria-disabled='true'] {
    cursor: default;
  }

  &:disabled {
    cursor: default;
  }
}

.common-tabs-mask {
  position: absolute;
  top: 0;
  right: 0;
  width: var(--common-tabs-mask-width);
  height: 100%;
  pointer-events: none;
  background: linear-gradient(270deg, #fff 25%, rgba(255, 255, 255, 0) 100%);

  :global([dir='rtl']) & {
    right: auto;
    left: 0;
    background: linear-gradient(90deg, #fff 25%, rgba(255, 255, 255, 0) 100%);
  }
}

@media screen and (max-width: 1200px) {
  .common-tabs-wrap,
  .common-tabs {
    width: 100%;
    max-width: 100%;
  }
}
</style>
